import React, {useState} from 'react';
import PropTypes, {InferProps} from "prop-types";
import {Button, Form} from 'react-bootstrap';
import {Account, SecondaryAuthorizationData, SecondaryIdentifier, sendSecondaryAuthorizationData} from "./api";
import CurrentState from "./CurrentStateEnum";
import WarningNoAccount from "./WarningNoAccount";
import BackModal from "../Modal";
import SecondaryIdType from "../SecondaryIdType";
import {useTranslation} from "react-i18next";

export default function SecondaryAuthorizationForm({
                                                       setSecondaryAuthorizationData,
                                                       secondaryAuthorizationData,
                                                       accounts,
                                                       setCurrentState,
                                                       setError,
                                                       setRendering,
                                                       renderError,
                                                       renderSpinner,
                                                       customerName
                                                   }: InferProps<typeof SecondaryAuthorizationForm.propTypes>) {

    let secondaryAuthorization : SecondaryAuthorizationData = secondaryAuthorizationData;

    const {t} = useTranslation();

    const accountList = accounts.map((account, key) => {
        let currency = account.currency;
        let accountNumber = account.accountNumber;

        return <option key={key} data-currency={currency} data-accountnumber={accountNumber}>{getValue(accountNumber, currency)}</option>;
    });

    const idTypes = Object.values(SecondaryIdType).map((value, key) => {
        return <option key={key}>{t(value)}</option>;
    });

    const [modalShow, setModalShow] = useState(false);
    const [secondaryIdType, setSecondaryIdType] = useState(SecondaryIdType.mobilePhone);

    const handleAccountChange = (event: React.FormEvent<HTMLSelectElement>) => {
        let select = event.currentTarget;

        let currency : any = select.options[select.selectedIndex].dataset.currency;

        if(currency === undefined){
            currency = null;
        }

        setSecondaryAuthorizationData(
            {
                account: {accountNumber: select.options[select.selectedIndex].dataset.accountnumber, currency: currency} as Account,
                secondaryIdentifier: {
                    phoneNumber: secondaryAuthorization.secondaryIdentifier.phoneNumber,
                    email: secondaryAuthorization.secondaryIdentifier.email
                } as SecondaryIdentifier
            } as SecondaryAuthorizationData
        );
    };

    const handleIdTypeChange = (event: React.FormEvent<HTMLSelectElement>) => {
        let inputValue: string = event.currentTarget.value;

        if(inputValue !== t(secondaryIdType)){
            if(secondaryIdType === SecondaryIdType.mobilePhone){
                setSecondaryIdType(SecondaryIdType.email);
            } else {
                setSecondaryIdType(SecondaryIdType.mobilePhone);
            }
        }
    };

    const validPhoneInputs = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "+", "-"];

    const setFormData = (inputValue : string) =>{
        let phoneNumberFromInput = '+36-';
        let emailFromInput = "";

        if(secondaryIdType === SecondaryIdType.mobilePhone){
            phoneNumberFromInput = inputValue;
        } else{
            emailFromInput = inputValue;
        }


        setSecondaryAuthorizationData(
            {
                account: secondaryAuthorization.account,
                secondaryIdentifier: {
                    phoneNumber: phoneNumberFromInput,
                    email: emailFromInput
                } as SecondaryIdentifier
            } as SecondaryAuthorizationData
        );
    };

    const handleSecondaryIdentifierChange = (event: React.FormEvent<HTMLSelectElement>) => {
        let inputValue: string = event.currentTarget.value;

        if(secondaryIdType === SecondaryIdType.mobilePhone) {
            if (validPhoneInputs.includes(inputValue.charAt(inputValue.length - 1))) {

                if(secondaryAuthorization.secondaryIdentifier.email !== ""){
                    secondaryAuthorization.secondaryIdentifier.email = "";
                }

                setFormData(inputValue);
            }
        } else{
            setFormData(inputValue);
        }

    };

    const submit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        setRendering(true);

        const response = await sendSecondaryAuthorizationData(secondaryAuthorization);

        if (response === null) {
            setError(t("helper.errorMessage"));
            setRendering(false);
        } else {
            if (response.errorMessage === null) {
                secondaryAuthorization.registrationId = response.registrationId;
                setSecondaryAuthorizationData(secondaryAuthorization);
                setRendering(false);
                setError("");
                setCurrentState(CurrentState.OTPForm);
            } else {
                if(response.status === "SECURITY_VIOLATION"){
                    setError(t("helper.sessionMessage"));
                    setRendering(false);
                    setCurrentState(CurrentState.FirstLoginForm);
                } else{
                    setError(t(response.errorMessage));
                    setRendering(false);
                }
            }
        }

    };

    const getType = () => {
        return SecondaryIdType.email === secondaryIdType ? "email" : "";
    };

    function getValue(accountNumber: string, currency: string | null) {
        if(currency !== null){
            return accountNumber + " " + currency;
        }

        return accountNumber;
    }

    const renderForm = ()=> {
        if(accounts.length === 0){
            return (
                <WarningNoAccount />
            )
        }

        return (
            <div>
                <div className="container">
                    <p className={"header"}>{t("secondaryAuth.title")}</p>
                    <p className={"description"}>{t("secondaryAuth.description")}</p>
                    <p>
                        {t("secondaryAuth.customerName") + ": " + customerName}<br />
                    </p>
                </div>
                <Form
                    onSubmit={((event: React.FormEvent<HTMLFormElement>) => submit(event))}
                >
                    {renderError()}
                    <Form.Group controlId="selectedAccount">
                        <Form.Label>{t("secondaryAuth.account")}</Form.Label>
                        <Form.Control
                            as="select"
                            value={getValue(secondaryAuthorization.account.accountNumber, secondaryAuthorization.account.currency)}
                            onChange={(event) => handleAccountChange(event as any)}>
                            {accountList}
                        </Form.Control>
                    </Form.Group>
                    <Form.Group controlId="secondaryIdType">
                        <Form.Label>{t("secondaryAuth.secondaryAuthType")}</Form.Label>
                        <Form.Control
                            as="select"
                            value={t(secondaryIdType) as string}
                            onChange={(event) => handleIdTypeChange(event as any)}>
                            {idTypes}
                        </Form.Control>
                    </Form.Group>
                    <Form.Group controlId="secondaryId">
                        <Form.Label>{secondaryIdType === SecondaryIdType.mobilePhone ? t("secondaryAuth.phoneNumber") : t("secondaryAuth.email")}</Form.Label>
                        <Form.Control
                            type={getType()}
                            value={secondaryIdType === SecondaryIdType.mobilePhone ? secondaryAuthorization.secondaryIdentifier.phoneNumber : secondaryAuthorization.secondaryIdentifier.email }
                            onChange={(event) => handleSecondaryIdentifierChange(event as any)}/>
                    </Form.Group>
                    <Form.Group controlId="termsAndConditions">
                        <Form.Check
                            type="checkbox"
                            required
                            label={
                                <Form.Label>
                                    {t("secondaryAuth.statement")}
                                </Form.Label>}/>
                    </Form.Group>
                    <Form.Group controlId="consent">
                        <Form.Check
                            type="checkbox"
                            required
                            label={
                                <Form.Label>
                                    {t("secondaryAuth.longStatement0")}<a rel="noopener noreferrer" target="_blank" href={t("helper.link")}>{t("secondaryAuth.termsAndConditions")}</a>{t("secondaryAuth.longStatement1")}
                                </Form.Label>}/>
                    </Form.Group>
                    <Form.Group controlId="consentAgree" className="consent-agree-group">
                        <Form.Check
                            type="checkbox"
                            required
                            label={
                                <Form.Label className="consent-label">
                                    <p className="consent-statement">{t("secondaryAuth.consentStatement")}</p>
                                    <p className="consent-point">{t("secondaryAuth.longStatement3")}</p>
                                    <p className="consent-point">{t("secondaryAuth.longStatement4")}</p>
                                    <p className="consent-point">{t("secondaryAuth.longStatement5")}</p>
                                </Form.Label>}/>
                    </Form.Group>

                    <div className={"btn-group"}>
                        <Button
                            onClick={() => setModalShow(true)}
                        >
                            {t("helper.back")}
                        </Button>
                        <Button
                            type="submit">
                            {renderSpinner()}
                            {t("helper.next")}
                        </Button>

                        <BackModal
                            show={modalShow}
                            onHide={()=> setModalShow(false)}
                            onConfirm={()=> setCurrentState(CurrentState.FirstLoginForm)}
                            back={t("helper.cancel")}
                            modalBody={t("modal.body")}
                            modalTitle={t("secondaryAuth.title")}
                            yes={t("helper.yes")}/>
                    </div>
                </Form>
            </div>
        )
    };

    return renderForm();

}

SecondaryAuthorizationForm.propTypes = {
    accounts: PropTypes.array.isRequired,
    setCurrentState: PropTypes.func.isRequired,
    setError: PropTypes.func.isRequired,
    setRendering: PropTypes.func.isRequired,
    renderError: PropTypes.func.isRequired,
    renderSpinner: PropTypes.func.isRequired,
    customerName: PropTypes.string.isRequired,
    secondaryAuthorizationData: PropTypes.any.isRequired,
    setSecondaryAuthorizationData: PropTypes.func.isRequired
};
